import AddIcon from "@mui/icons-material/Add";
import { Button, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import React from "react";
import { useGetRecordId, useRecordContext } from "react-admin";
import { Controller, FormProvider, useForm } from "react-hook-form";
import sendRequest from "../../dataProvider/sendRequest";
import CustomProductSelect from "../../unit/forms/CustomProductSelect";
import MuiCheckboxGroup from "../../unit/forms/MuiCheckboxGroup";
import useNotify from "../../unit/useNotify";
import SectionStyled from "./SectionStyled";
import SectionTitle from "./SectionTitle";
import CustomProductActionBottom from "../CustomProductActionBottom";
import { NoContentSet } from "./NoContentSet";

const Purpose = ({ onSave, defaultValues, ...props }: any) => {
  const record = useRecordContext();
  const pId = useGetRecordId();
  const data = record?.data?.basic?.purpose;
  const general = data?.general;
  const designated = data?.designated;
  const { success, error } = useNotify();
  const subType = record?.data?.basic?.subType;
  const productModulesGroup = record?.data?.basic?.productModulesGroup;
  const checkedPurpose = data?.checkedPurpose;
  const checkedPurposeDesignated = data?.checkedPurposeDesignated;

  const [purposeChoices, setPurposeChoices] = React.useState(
    general?.purposeList || []
  );
  const [purposeChoicesDesignated, setPurposeChoicesDesignated] =
    React.useState(designated?.purposeList || []);

  // 保存初始值 ref，用于比较脏状态
  const defaultValuesRef = React.useRef<any>(null);

  // 构建初始值的函数
  const buildInitialValues = () => {
    return {
      loan_purpose: checkedPurpose || [],
      loan_purpose_designated: checkedPurposeDesignated || [],
      sAskDespPurpose: data?.sAskDespPurpose || "",
    };
  };

  // 初始化表单
  const methods = useForm({
    mode: "onChange",
    defaultValues: buildInitialValues(),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    reset,
    getValues,
    setValue,
    watch,
  } = methods;

  // 当数据加载时更新表单和默认值
  React.useEffect(() => {
    if (data) {
      const newValues = buildInitialValues();
      console.log("数据加载，重置表单:", newValues);

      // 保存默认值
      defaultValuesRef.current = JSON.parse(JSON.stringify(newValues));

      // 重置表单
      reset(newValues, {
        keepDirty: false,
        keepValues: false,
      });
    }
  }, [data, reset]);

  // 手动计算实际脏状态（解决 isDirty 不准确的问题）
  const actualIsDirty = React.useMemo(() => {
    if (!defaultValuesRef.current) return isDirty;

    const currentValues = getValues();
    const defaultVals = defaultValuesRef.current;

    return Object.keys(currentValues).some((key) => {
      const currentVal = currentValues[key];
      const defaultVal = defaultVals[key];

      // 处理数组的比较
      if (Array.isArray(currentVal) && Array.isArray(defaultVal)) {
        return JSON.stringify(currentVal) !== JSON.stringify(defaultVal);
      }
      return currentVal !== defaultVal;
    });
  }, [watch(), isDirty, getValues]);

  // 监听 isDirty 变化，通知父组件
  React.useEffect(() => {
    console.log(
      "Purpose isDirty:",
      actualIsDirty,
      "原始 isDirty:",
      isDirty,
      "dirtyFields:",
      dirtyFields
    );

    if (props?.onDirtyChange) {
      props?.onDirtyChange(props?.curMenu, actualIsDirty);
    }
  }, [actualIsDirty, isDirty, dirtyFields, props?.curMenu]);

  // 调试：监听表单变化
  React.useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      console.log("表单数据变化:", {
        name: name,
        value: value,
        type: type,
        actualIsDirty: actualIsDirty,
        dirtyFields: dirtyFields,
      });
    });

    return () => subscription.unsubscribe();
  }, [watch, actualIsDirty, dirtyFields]);

  const handleSaveClick = handleSubmit((formData) => {
    formData.saveAction = "purpose";
    console.log("保存数据:", formData);

    if (onSave) {
      onSave(formData, props?.curMenu);

      // 保存后更新默认值并重置
      defaultValuesRef.current = JSON.parse(JSON.stringify(formData));
      reset(formData, {
        keepDirty: false,
        keepValues: false,
      });
    }
  });

  const addNewPurpose = () => {
    const newPurpose = getValues("newPurpose");
    if (!newPurpose) return;

    const formData = getValues();
    formData.saveAction = "purpose";
    formData.saveActionType = "newPurpose";

    if (formData) {
      const reqUrl = "products/actions/edit";
      sendRequest(reqUrl, { id: pId, data: formData }).then((res) => {
        if (res?.code == 200) {
          success("Saved successfully: " + res.data.purposeId);

          // 更新选项列表
          const newPurposeItem = { id: res.data.purposeId, name: newPurpose };
          setPurposeChoices((prev) => [...prev, newPurposeItem]);

          // 更新表单值
          const currentValues = getValues();
          const newLoanPurpose = [
            ...(currentValues.loan_purpose || []),
            res.data.purposeId,
          ];
          setValue("loan_purpose", newLoanPurpose, { shouldDirty: true });

          // 注意：不更新 defaultValuesRef，因为这是新添加的，应该算作修改
        } else {
          error("Error saving: " + res?.message);
        }
      });
    }

    setValue("newPurpose", "");
  };

  const addNewPurpose2 = () => {
    const newPurpose2 = getValues("newPurpose2");
    if (!newPurpose2) return;

    const formData = getValues();
    formData.saveAction = "purpose";
    formData.saveActionType = "newPurpose2";

    if (formData) {
      const reqUrl = "products/actions/edit";
      sendRequest(reqUrl, { id: pId, data: formData }).then((res) => {
        if (res?.code == 200) {
          success("Saved successfully: " + res.data.purposeId);

          // 更新选项列表
          const newPurposeItem = { id: res.data.purposeId, name: newPurpose2 };
          setPurposeChoicesDesignated((prev) => [...prev, newPurposeItem]);

          // 更新表单值
          const currentValues = getValues();
          const newLoanPurposeDesignated = [
            ...(currentValues.loan_purpose_designated || []),
            res.data.purposeId,
          ];
          setValue("loan_purpose_designated", newLoanPurposeDesignated, {
            shouldDirty: true,
          });
        } else {
          error("Error saving: " + res?.message);
        }
      });
    }

    setValue("newPurpose2", "");
  };

  // 手动重置按钮
  const handleReset = () => {
    if (defaultValuesRef.current) {
      console.log("手动重置到:", defaultValuesRef.current);
      reset(defaultValuesRef.current, {
        keepDirty: false,
        keepValues: false,
      });
    }
  };

  const AskPurposeLoan = [
    "start",
    "employee",
    "general",
    "refund",
    "micro",
    "smallmicro",
    "smallpaycheckprotection",
  ];

  return (
    <FormProvider {...methods}>
      <SectionStyled id="section_purpose">
        <SectionTitle title="Purpose - Select permitted purposes for the loan" />

        <Box
          sx={{
            padding: "10px",
            height: "calc(100vh - 255px)",
            overflowY: "auto",
          }}
        >
          {!productModulesGroup?.includes("credit_purpose") &&
            !productModulesGroup?.includes("purpose_general") &&
            !productModulesGroup?.includes("designated_purchase") && (
              <NoContentSet />
            )}

          {(productModulesGroup?.includes("credit_purpose") ||
            productModulesGroup?.includes("purpose_general")) && (
            <>
              <Typography
                variant="h3"
                color="text.secondary"
                sx={{ marginTop: "20px" }}
              >
                General purpose: Select all permissible purposes
              </Typography>
              <Box sx={{ width: "100%", paddingLeft: "50px" }}>
                <Controller
                  name="loan_purpose"
                  control={control}
                  render={({ field }) => (
                    <MuiCheckboxGroup {...field} choices={purposeChoices} />
                  )}
                />
              </Box>
              <Box
                display="flex"
                alignItems="center"
                sx={{ width: "100%", paddingLeft: "50px" }}
              >
                <Controller
                  name="newPurpose"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Add permissible purchase item"
                      variant="outlined"
                      size="small"
                      sx={{ marginBottom: "10px", maxWidth: "300px" }}
                    />
                  )}
                />
                <Button
                  onClick={addNewPurpose}
                  variant="outlined"
                  sx={{
                    margin: "10px",
                    color: "primary",
                  }}
                >
                  <AddIcon />
                </Button>
              </Box>
            </>
          )}

          {/* start Designated */}
          {productModulesGroup?.includes("designated_purchase") && (
            <>
              <Typography
                variant="h3"
                color="text.secondary"
                sx={{ marginTop: "20px" }}
              >
                Designated purpose: Select the one that applies
              </Typography>
              <Box sx={{ width: "100%", paddingLeft: "50px" }}>
                <Controller
                  name="loan_purpose_designated"
                  control={control}
                  render={({ field }) => (
                    <MuiCheckboxGroup
                      {...field}
                      choices={purposeChoicesDesignated}
                    />
                  )}
                />
              </Box>
              <Box
                display="flex"
                alignItems="center"
                sx={{ width: "100%", paddingLeft: "50px" }}
              >
                <Controller
                  name="newPurpose2"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Add permissible purchase item"
                      variant="outlined"
                      size="small"
                      sx={{ marginBottom: "10px", maxWidth: "300px" }}
                    />
                  )}
                />
                <Button
                  onClick={addNewPurpose2}
                  variant="outlined"
                  sx={{
                    margin: "10px",
                    color: "primary",
                  }}
                >
                  <AddIcon />
                </Button>
              </Box>
            </>
          )}

          {/* end Designated */}
          {productModulesGroup?.includes("planned_use_of_funds") &&
            AskPurposeLoan?.includes(subType) && (
              <Box sx={{ marginTop: "50px" }}>
                <Controller
                  name="sAskDespPurpose"
                  control={control}
                  render={({ field }) => (
                    <CustomProductSelect
                      {...field}
                      fullWidth
                      label="Ask for description of planned use of funds"
                      sx={{ maxWidth: "400px" }}
                      variant="outlined"
                      optionlist={[
                        { value: "N/A", label: "No Need" },
                        { value: "Required", label: "Required" },
                        { value: "Optional", label: "Optional" },
                      ]}
                    />
                  )}
                />
              </Box>
            )}
        </Box>

        {!productModulesGroup?.includes("credit_purpose") &&
          !productModulesGroup?.includes("designated_purchase") && (
            <CustomProductActionBottom>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveClick}
                  disabled={!actualIsDirty}
                  sx={{ width: "120px", mt: 1 }}
                >
                  Save
                </Button>
              </Box>
            </CustomProductActionBottom>
          )}
      </SectionStyled>
    </FormProvider>
  );
};

export default Purpose;
